// GrowthOverview.js — Admin Growth Intelligence Overview
// Renders school-wide growth stats, score distribution, domain averages, and recent reports.

import React, { useState, useEffect } from "react";
import { Spin, message } from "antd";
import { motion } from "framer-motion";
import {
    TrendingUp,
    Users,
    Eye,
    MessageSquareText,
    BarChart3,
    FileText,
} from "lucide-react";
import { API_BASE_URL } from "../../config";
import "./GrowthOverview.css";

// ─── Constants ─────────────────────────────────────────────────────────────────

const DOMAIN_COLORS = {
    academics:    "#1565c0",
    skills:       "#7b1fa2",
    competencies: "#00838f",
    health:       "#2e7d32",
    career:       "#e65100",
    tech:         "#283593",
    values:       "#c62828",
};

const DOMAIN_LABELS = {
    academics:    "Academics",
    skills:       "Skills",
    competencies: "Competencies",
    health:       "Health",
    career:       "Career",
    tech:         "Technology",
    values:       "Values",
};

const LEVEL_CONFIG = [
    { key: "role_model",    label: "Role Model",     color: "#7b1fa2" },
    { key: "strong",        label: "Strong",          color: "#4caf50" },
    { key: "mostly_right",  label: "Mostly Right",    color: "#2196f3" },
    { key: "needs_support", label: "Needs Support",   color: "#ff9800" },
    { key: "starting",      label: "Starting",        color: "#f44336" },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Returns a hex color for a numeric score (1–5 scale).
 */
const getScoreColor = (score) => {
    if (score >= 4.4) return "#7b1fa2";
    if (score >= 3.7) return "#4caf50";
    if (score >= 3.0) return "#2196f3";
    if (score >= 2.0) return "#ff9800";
    return "#f44336";
};

/**
 * Returns the badge background and text color pair for a level string.
 */
const getLevelBadgeStyle = (level = "") => {
    const map = {
        "role model":    { bg: "#f3e5f5", color: "#7b1fa2" },
        "strong":        { bg: "#e8f5e9", color: "#2e7d32" },
        "mostly right":  { bg: "#e3f2fd", color: "#1565c0" },
        "needs support": { bg: "#fff3e0", color: "#e65100" },
        "starting":      { bg: "#ffebee", color: "#c62828" },
    };
    return map[level.toLowerCase()] || { bg: "#f5f5f5", color: "#555" };
};

/**
 * Formats an ISO date string as a localised short date.
 */
const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, subLabel, accent }) => (
    <motion.div
        className="go-stat-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
    >
        <div className="go-stat-icon" style={{ background: `${accent}18`, color: accent }}>
            <Icon size={22} />
        </div>
        <div className="go-stat-text">
            <div className="go-stat-value">{value}</div>
            <div className="go-stat-label">{label}</div>
            {subLabel && <div className="go-stat-sub">{subLabel}</div>}
        </div>
    </motion.div>
);

const HorizontalBar = ({ label, count, total, color, delay = 0 }) => {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="go-bar-row">
            <div className="go-bar-label">{label}</div>
            <div className="go-bar-track">
                <motion.div
                    className="go-bar-fill"
                    style={{ background: color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.65, delay }}
                />
            </div>
            <div className="go-bar-value" style={{ color }}>
                {count}
                <span className="go-bar-pct"> ({pct}%)</span>
            </div>
        </div>
    );
};

const DomainBar = ({ domainKey, score, delay = 0 }) => {
    const color = DOMAIN_COLORS[domainKey] || "#888";
    const label = DOMAIN_LABELS[domainKey] || domainKey;
    const pct   = Math.min((score / 5) * 100, 100);
    return (
        <div className="go-bar-row">
            <div className="go-bar-label go-bar-label--domain">{label}</div>
            <div className="go-bar-track">
                <motion.div
                    className="go-bar-fill"
                    style={{ background: color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.65, delay }}
                />
            </div>
            <div className="go-bar-value go-bar-value--domain" style={{ color }}>
                {typeof score === "number" ? score.toFixed(2) : "—"}
                <span className="go-bar-pct"> / 5</span>
            </div>
        </div>
    );
};

// ─── Main Component ─────────────────────────────────────────────────────────────

const GrowthOverview = () => {
    const [data,          setData]          = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [selectedClass, setSelectedClass] = useState("");

    const accessToken = localStorage.getItem("accessToken");

    // Re-fetch whenever the class filter changes.
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClass]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = selectedClass ? `?class_id=${selectedClass}` : "";
            const res = await fetch(`${API_BASE_URL}/admin/growth-overview${params}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!res.ok) throw new Error("Failed");
            setData(await res.json());
        } catch {
            message.error("Failed to load growth overview");
        }
        setLoading(false);
    };

    // ── Derived values ──────────────────────────────────────────────────────────
    const classList         = data?.class_list || [];
    const stats             = data?.stats || {};
    const scoreDistribution = data?.score_distribution || {};
    const domainAverages    = data?.domain_averages || {};
    const recentReports     = data?.recent_reports || [];

    const totalStudents        = stats.total_students        ?? 0;
    const studentsWithReports  = stats.students_with_reports ?? 0;
    const totalObservations    = stats.total_observations    ?? 0;
    const totalReflections     = stats.total_reflections     ?? 0;

    // Total across all distribution levels for percentage calculation
    const distributionTotal = LEVEL_CONFIG.reduce(
        (acc, l) => acc + (scoreDistribution[l.key] ?? 0),
        0
    );

    // ── Render ──────────────────────────────────────────────────────────────────

    return (
        <>
            <div className="go-container">

                {/* ── Header ── */}
                <motion.div
                    className="go-header"
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="go-header-icon">
                        <TrendingUp size={28} color="#D67C2C" />
                    </div>
                    <div>
                        <h1>Growth Intelligence Overview</h1>
                        <p className="go-header-sub">
                            School-wide holistic growth analytics and report coverage
                        </p>
                    </div>
                </motion.div>

                {/* ── Class Filter ── */}
                <div className="go-class-filter">
                    <label className="go-filter-label">Filter by Class</label>
                    <select
                        className="go-select"
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">All Classes</option>
                        {classList.map((cls) => (
                            <option key={cls.id || cls._id} value={cls.id || cls._id}>
                                {cls.name || cls.class_name || `${cls.grade}-${cls.section}`}
                            </option>
                        ))}
                    </select>
                    {loading && <Spin size="small" />}
                </div>

                {/* ── Loading / Empty Gate ── */}
                {loading && !data ? (
                    <div className="go-loading">
                        <Spin size="large" />
                        <p>Loading growth data…</p>
                    </div>
                ) : !data ? (
                    <div className="go-empty">
                        <BarChart3 size={56} color="#ccc" />
                        <h3>No data available</h3>
                        <p>Growth overview data could not be loaded. Please try again.</p>
                    </div>
                ) : (
                    <>
                        {/* ── Stats Row ── */}
                        <div className="go-stats-row">
                            <StatCard
                                icon={Users}
                                label="Total Students"
                                value={totalStudents.toLocaleString()}
                                accent="#1565c0"
                            />
                            <StatCard
                                icon={FileText}
                                label="Reports Generated"
                                value={studentsWithReports.toLocaleString()}
                                subLabel={`of ${totalStudents} students`}
                                accent="#D67C2C"
                            />
                            <StatCard
                                icon={Eye}
                                label="Total Observations"
                                value={totalObservations.toLocaleString()}
                                accent="#00838f"
                            />
                            <StatCard
                                icon={MessageSquareText}
                                label="Total Reflections"
                                value={totalReflections.toLocaleString()}
                                accent="#7b1fa2"
                            />
                        </div>

                        {/* ── Score Distribution ── */}
                        <motion.div
                            className="go-section"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.15 }}
                        >
                            <div className="go-section-header">
                                <BarChart3 size={18} color="#D67C2C" />
                                <h2>Score Distribution</h2>
                            </div>
                            <p className="go-section-sub">
                                Distribution of students across performance levels
                            </p>
                            <div className="go-bars-wrapper">
                                {LEVEL_CONFIG.map((lvl, i) => (
                                    <HorizontalBar
                                        key={lvl.key}
                                        label={lvl.label}
                                        count={scoreDistribution[lvl.key] ?? 0}
                                        total={distributionTotal}
                                        color={lvl.color}
                                        delay={i * 0.07}
                                    />
                                ))}
                            </div>
                        </motion.div>

                        {/* ── Domain Averages ── */}
                        <motion.div
                            className="go-section"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.25 }}
                        >
                            <div className="go-section-header">
                                <TrendingUp size={18} color="#D67C2C" />
                                <h2>Domain Averages</h2>
                            </div>
                            <p className="go-section-sub">
                                School-wide average composite score per holistic domain (out of 5)
                            </p>
                            <div className="go-bars-wrapper">
                                {Object.keys(DOMAIN_LABELS).map((key, i) => {
                                    const score = domainAverages[key] ?? 0;
                                    return (
                                        <DomainBar
                                            key={key}
                                            domainKey={key}
                                            score={score}
                                            delay={i * 0.07}
                                        />
                                    );
                                })}
                            </div>
                        </motion.div>

                        {/* ── Recent Reports Table ── */}
                        <motion.div
                            className="go-section"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.35 }}
                        >
                            <div className="go-section-header">
                                <FileText size={18} color="#D67C2C" />
                                <h2>Recent Reports</h2>
                            </div>
                            <p className="go-section-sub">
                                Last 10 growth reports generated across the school
                            </p>

                            {recentReports.length === 0 ? (
                                <div className="go-empty go-empty--inline">
                                    <FileText size={40} color="#ccc" />
                                    <p>No reports have been generated yet.</p>
                                </div>
                            ) : (
                                <div className="go-table-wrapper">
                                    <table className="go-table">
                                        <thead>
                                            <tr>
                                                <th>Student Name</th>
                                                <th>Grade</th>
                                                <th>Section</th>
                                                <th>Score</th>
                                                <th>Level</th>
                                                <th>Period</th>
                                                <th>Generated On</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentReports.slice(0, 10).map((report, i) => {
                                                const scoreColor   = getScoreColor(report.overall_score ?? 0);
                                                const levelStyle   = getLevelBadgeStyle(report.overall_level ?? "");
                                                return (
                                                    <motion.tr
                                                        key={report.report_id || report.student_id || i}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: i * 0.04 }}
                                                    >
                                                        <td className="go-td-name">
                                                            {report.student_name || "—"}
                                                        </td>
                                                        <td>{report.grade ?? "—"}</td>
                                                        <td>{report.section ?? "—"}</td>
                                                        <td>
                                                            <span
                                                                className="go-score-badge"
                                                                style={{
                                                                    background: `${scoreColor}18`,
                                                                    color: scoreColor,
                                                                    border: `1px solid ${scoreColor}44`,
                                                                }}
                                                            >
                                                                {report.overall_score != null
                                                                    ? report.overall_score.toFixed(2)
                                                                    : "—"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className="go-score-badge"
                                                                style={{
                                                                    background: levelStyle.bg,
                                                                    color: levelStyle.color,
                                                                }}
                                                            >
                                                                {report.overall_level || "—"}
                                                            </span>
                                                        </td>
                                                        <td className="go-td-muted">
                                                            {report.report_period || "—"}
                                                        </td>
                                                        <td className="go-td-muted">
                                                            {formatDate(report.generated_at)}
                                                        </td>
                                                    </motion.tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </motion.div>
                    </>
                )}
            </div>
        </>
    );
};

export default GrowthOverview;
